import type { BackendModuleDescriptor, FrontModule } from "@nabble/sdk";
import { useEffect, useMemo, useState } from "react";
import { fetchMyModules } from "../api/me";
import { useAuth } from "../auth/AuthContext";
import { type AppModule, buildAppModules } from "../layout/appModules";
import { resolveVisibleModules, warnUnknownBackendModules } from "../modules";

export interface VisibleModules {
  /** Modules in the bundle that the backend also returns for this user. */
  modules: FrontModule[];
  /** Modules as the user sees them (sub-sections filtered by permission, Admin Center for admins). */
  appModules: AppModule[];
  modulesLoading: boolean;
}

/** Loads /api/me/modules once and intersects it with the modules compiled into the shell. */
export function useVisibleModules(): VisibleModules {
  const { user } = useAuth();
  const [backendModules, setBackendModules] = useState<BackendModuleDescriptor[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);

  useEffect(() => {
    fetchMyModules()
      .then((data) => {
        warnUnknownBackendModules(data);
        setBackendModules(data);
      })
      .catch(() => setBackendModules([]))
      .finally(() => setModulesLoading(false));
  }, []);

  const permissions = useMemo(() => user?.permissions ?? [], [user]);
  const modules = useMemo(() => resolveVisibleModules(backendModules, permissions), [backendModules, permissions]);
  const appModules = useMemo(() => buildAppModules(modules, permissions), [modules, permissions]);
  return { modules, appModules, modulesLoading };
}
